import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMember } from '../contexts/MemberContext';
import { createMemberProfile } from '../utils/supabase';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { startMembership } = useMember();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processSuccess = async () => {
      // Get session ID from URL (Payment Links may not provide this; it's optional)
      const sessionId = searchParams.get('session_id');

      // Read pending signup data saved before redirecting to Stripe
      let pending = null;
      try {
        pending = JSON.parse(localStorage.getItem('pendingSignupData') || 'null');
      } catch (_) {
        pending = null;
      }

      // Note: Member creation is now handled entirely by Stripe webhook
      // The webhook receives all signup data via Stripe metadata and creates the complete member record
      // This eliminates race conditions and ensures all data (including stripe_customer_id) is saved correctly
      console.log('[SubscriptionSuccess] Member created by webhook with all data from Stripe metadata');

      // Ensure userEmail is stored (may have been lost during Stripe redirect)
      if (pending?.email && !localStorage.getItem('userEmail')) {
        localStorage.setItem('userEmail', pending.email);
        console.log('[SubscriptionSuccess] Restored userEmail from pending signup data:', pending.email);
      }

      // Activate membership locally
      localStorage.setItem('memberStatus', 'active');
      localStorage.setItem('subscriptionDate', new Date().toISOString());
      if (sessionId) localStorage.setItem('stripeSessionId', sessionId);

      // Initialize member tracking for milestones
      if (!localStorage.getItem('memberJoinDate')) {
        localStorage.setItem('memberJoinDate', new Date().toISOString());
      }

      // Mark that user should see welcome modal on their first navigation
      localStorage.setItem('showWelcomeOnFirstNav', 'true');

      // Clear pending data now that it has been persisted
      localStorage.removeItem('pendingSignupData');

      // Start membership via context
      startMembership({
        planId: 'freedom',
        waiverAccepted: true,
        termsAccepted: true,
        source: 'stripe',
      });

      setIsProcessing(false);
    };

    // Small delay for UX consistency, then process
    const t = setTimeout(() => { processSuccess(); }, 700);
    return () => clearTimeout(t);
  }, [searchParams, startMembership]);

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 to-green-50 flex items-center justify-center p-6">
        <motion.div 
          className="bg-white rounded-2xl p-8 shadow-lg text-center max-w-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="text-5xl mb-4">⏳</div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#a8b5a0' }}>
            Processing Your Subscription...
          </h1>
          <p className="text-gray-600">
            Please wait while we set up your account.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-green-50 flex items-center justify-center p-6 pt-24">
      <motion.div 
        className="bg-white rounded-2xl p-8 shadow-lg text-center max-w-md"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div 
          className="text-6xl mb-4"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          🎉
        </motion.div>
        
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#a8b5a0' }}>
          Welcome to Alera!
        </h1>
        
        <p className="text-lg text-gray-600 mb-6">
          Your subscription is active. Welcome to your wellness community!
        </p>

        <div className="bg-rose-50 border-2 rounded-xl p-6 mb-6" style={{ borderColor: '#f0e0dc' }}>
          <h2 className="font-bold text-charcoal-900 mb-3 text-lg">Check Your Email</h2>
          <p className="text-sm text-charcoal-700 mb-2">
            We've sent you an email with a link to set your password. Once you've set your password, you can log in and start your wellness journey.
          </p>
          <p className="text-xs text-charcoal-600 italic">
            Don't see it? Check your spam folder or use "Forgot Password" on the login page.
          </p>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={() => navigate('/login')}
            style={{ backgroundColor: '#a8b5a0' }}
            className="w-full text-white py-3 px-6 rounded-xl font-medium hover:opacity-90 transition-all shadow-md"
          >
            Go to Login Page
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full border-2 border-gray-300 text-gray-700 py-3 px-6 rounded-xl font-medium hover:bg-gray-50 transition-all"
          >
            Back to Home
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-6">
          Once you've set your password, you can log in and start your wellness journey!
        </p>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
